'use client';

import { type ReactNode, useId, useState } from 'react';

import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@kit/ui/popover';
import { RadioGroup, RadioGroupItem } from '@kit/ui/radio-group';
import { cn } from '@kit/ui/utils';

import {
  ADULT_AGE,
  type ClassFilterState,
  EMPTY_CLASS_FILTERS,
  type FieldFilter,
  type FieldFilterValue,
  type FilterableClass,
  type TimeOfDay,
  countActiveClassFilters,
  getClassFilterOptions,
} from '../_lib/class-filters';
import { useBookingPresentation } from './booking-presentation';
import styles from './native-booking-controls.module.css';
import { ScrollableChipRow } from './scrollable-chip-row';

interface ClassFiltersProps {
  classes: FilterableClass[];
  value: ClassFilterState;
  onChange: (value: ClassFilterState) => void;
}

/** Filter chips for a class list: required-field rules, day of week, time of day. */
export function ClassFilters({ classes, value, onChange }: ClassFiltersProps) {
  const { t, i18n } = useTranslation('bookings');
  const options = getClassFilterOptions(classes, value);
  const hasActiveFilters = countActiveClassFilters(value) > 0;
  const weekdayName = (day: number) =>
    new Intl.DateTimeFormat(i18n.language, {
      weekday: 'long',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(2026, 0, 4 + day, 12)));

  const setField = (
    field: FieldFilter,
    fieldValue: FieldFilterValue | null,
  ) => {
    const rest = Object.fromEntries(
      Object.entries(value.fields).filter(([id]) => id !== field.fieldId),
    );
    onChange({
      ...value,
      fields:
        fieldValue === null
          ? rest
          : { ...rest, [field.fieldId]: { field, value: fieldValue } },
    });
  };

  const hasSeveralAgeFields =
    options.fields.filter((field) => field.kind === 'age').length > 1;

  const chips = [
    ...options.fields.map((field) => (
      <FieldFilterChip
        key={field.fieldId}
        field={field}
        title={
          field.kind === 'age' && !hasSeveralAgeFields
            ? t('classFilters.age')
            : field.label
        }
        value={value.fields[field.fieldId]?.value ?? null}
        onApply={(fieldValue) => setField(field, fieldValue)}
      />
    )),
    options.weekdays.length > 1 || value.weekday !== null ? (
      <RadioFilterChip
        key="weekday"
        title={t('classFilters.dayOfWeek')}
        value={value.weekday === null ? null : String(value.weekday)}
        options={options.weekdays.map((day) => ({
          value: String(day),
          label: weekdayName(day),
        }))}
        onApply={(day) =>
          onChange({ ...value, weekday: day === null ? null : Number(day) })
        }
      />
    ) : null,
    options.timesOfDay.length > 1 || value.timeOfDay !== null ? (
      <RadioFilterChip
        key="time"
        title={t('classFilters.timeOfDay')}
        value={value.timeOfDay}
        options={options.timesOfDay.map((time) => ({
          value: time,
          label: t(`classFilters.times.${time}`),
          chipLabel: t(`classFilters.timesShort.${time}`),
        }))}
        onApply={(time) =>
          onChange({ ...value, timeOfDay: time as TimeOfDay | null })
        }
      />
    ) : null,
  ].filter(Boolean);

  // The row exists for the classes' custom-field requirements. With none,
  // day and time chips alone are noise, so the row only stays while a
  // filter is applied (so it can still be cleared).
  if (chips.length === 0 || (options.fields.length === 0 && !hasActiveFilters))
    return null;

  return (
    <div
      role="group"
      aria-label={t('classFilters.label')}
      className="-mx-1"
      data-test="class-filters"
    >
      <ScrollableChipRow
        backLabel={t('classFilters.scrollBack')}
        forwardLabel={t('classFilters.scrollForward')}
        testId="class-filters"
        contentClassName="items-center gap-2"
      >
        {chips}
        {countActiveClassFilters(value) > 0 ? (
          <Button
            type="button"
            variant="link"
            className="h-11 shrink-0 px-2"
            onClick={() => onChange(EMPTY_CLASS_FILTERS)}
            data-test="class-filters-clear"
          >
            {t('classFilters.clearAll')}
          </Button>
        ) : null}
      </ScrollableChipRow>
    </div>
  );
}

/** Empty state for a list whose classes are all hidden by the filters. */
export function NoMatchingClasses({ onClear }: { onClear: () => void }) {
  const { t } = useTranslation('bookings');

  return (
    <div
      className="bg-muted/30 space-y-2 rounded-lg border p-6 text-center"
      data-test="class-filters-empty"
    >
      <p className="text-muted-foreground text-sm">
        {t('classFilters.noMatches')}
      </p>
      <Button type="button" variant="outline" size="sm" onClick={onClear}>
        {t('classFilters.clearAll')}
      </Button>
    </div>
  );
}

function FieldFilterChip({
  field,
  title,
  value,
  onApply,
}: {
  field: FieldFilter;
  title: string;
  value: FieldFilterValue | null;
  onApply: (value: FieldFilterValue | null) => void;
}) {
  const { t } = useTranslation('bookings');

  if (field.kind === 'select' || field.kind === 'boolean') {
    const options =
      field.kind === 'boolean'
        ? [
            { value: 'true', label: t('classFilters.yes') },
            { value: 'false', label: t('classFilters.no') },
          ]
        : field.options.map((option) => ({ value: option, label: option }));

    return (
      <RadioFilterChip
        title={title}
        value={value === null ? null : String(value)}
        showTitleWithValue
        options={options}
        onApply={(selected) =>
          onApply(
            selected === null || field.kind === 'select'
              ? selected
              : selected === 'true',
          )
        }
      />
    );
  }

  if (field.kind === 'age') {
    return (
      <AgeFilterChip
        title={title}
        ages={Array.isArray(value) ? value : []}
        onApply={(ages) => onApply(ages.length > 0 ? ages : null)}
      />
    );
  }

  return (
    <FilterPopover
      title={title}
      chip={
        typeof value === 'number' ? (
          <ChipValue title={title} value={String(value)} />
        ) : (
          title
        )
      }
      isActive={value !== null}
      onReset={() => onApply(null)}
    >
      {() => (
        <Input
          type="number"
          inputMode="decimal"
          step="any"
          aria-label={title}
          placeholder={t('classFilters.valuePlaceholder')}
          value={typeof value === 'number' ? value : ''}
          onChange={(event) => {
            const next = event.target.valueAsNumber;
            onApply(Number.isFinite(next) ? next : null);
          }}
        />
      )}
    </FilterPopover>
  );
}

const AGE_TILES = Array.from({ length: ADULT_AGE + 1 }, (_, age) => age);

/**
 * Ages as tiles; several can be picked and each tap filters immediately.
 * "Under 1" is age 0 and "Adults (18+)" covers every adult age.
 */
function AgeFilterChip({
  title,
  ages,
  onApply,
}: {
  title: string;
  ages: number[];
  onApply: (ages: number[]) => void;
}) {
  const { t } = useTranslation('bookings');
  const ageLabel = (age: number) =>
    age === 0
      ? t('classFilters.ageUnderOne')
      : age >= ADULT_AGE
        ? t('classFilters.ageAdults', { age: ADULT_AGE })
        : String(age);
  const toggle = (age: number) =>
    onApply(
      ages.includes(age)
        ? ages.filter((selected) => selected !== age)
        : [...ages, age].sort((left, right) => left - right),
    );

  return (
    <FilterPopover
      title={title}
      chip={
        ages.length === 1 ? (
          <bdi>{ageLabel(ages[0]!)}</bdi>
        ) : (
          <>
            {title}
            {ages.length > 1 ? (
              <span className="bg-primary text-primary-foreground inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold">
                {ages.length}
              </span>
            ) : null}
          </>
        )
      }
      isActive={ages.length > 0}
      onReset={() => onApply([])}
    >
      {() => (
        <div role="group" aria-label={title} className="grid grid-cols-4 gap-2">
          {AGE_TILES.map((age) => {
            const isSelected = ages.includes(age);
            return (
              <button
                key={age}
                type="button"
                aria-pressed={isSelected}
                onClick={() => toggle(age)}
                className={cn(
                  styles.filterTile,
                  'hover:bg-muted/50 focus-visible:ring-ring flex h-12 items-center justify-center rounded-lg border px-1 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none',
                  age === 0 && 'text-xs',
                  age >= ADULT_AGE && 'col-span-2',
                  isSelected &&
                    'border-foreground bg-muted hover:bg-muted ring-foreground font-semibold ring-1',
                )}
                data-test="class-filter-age-tile"
              >
                {ageLabel(age)}
              </button>
            );
          })}
        </div>
      )}
    </FilterPopover>
  );
}

/** Single choice: picking an option filters right away and closes the popover. */
function RadioFilterChip({
  title,
  value,
  options,
  showTitleWithValue = false,
  onApply,
}: {
  title: string;
  value: string | null;
  options: Array<{ value: string; label: string; chipLabel?: string }>;
  showTitleWithValue?: boolean;
  onApply: (value: string | null) => void;
}) {
  const idPrefix = useId();
  const selected = options.find((option) => option.value === value);
  const selectedLabel = selected
    ? (selected.chipLabel ?? selected.label)
    : null;

  return (
    <FilterPopover
      title={title}
      chip={
        selectedLabel === null ? (
          title
        ) : showTitleWithValue ? (
          <ChipValue title={title} value={selectedLabel} />
        ) : (
          <bdi>{selectedLabel}</bdi>
        )
      }
      isActive={value !== null}
      onReset={() => onApply(null)}
    >
      {(close) => (
        <RadioGroup
          value={value ?? ''}
          onValueChange={(next) => {
            onApply(next);
            close();
          }}
          aria-label={title}
          className="gap-1"
        >
          {options.map((option, index) => {
            const id = `${idPrefix}-${index}`;
            return (
              <Label
                key={option.value}
                htmlFor={id}
                className="hover:bg-muted/50 flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2 text-sm font-normal"
              >
                <RadioGroupItem id={id} value={option.value} />
                {option.label}
              </Label>
            );
          })}
        </RadioGroup>
      )}
    </FilterPopover>
  );
}

function ChipValue({ title, value }: { title: string; value: string }) {
  return (
    <span>
      {title}: <bdi>{value}</bdi>
    </span>
  );
}

/** Chip + popover shell. Choices apply as they are made; Reset clears this filter. */
function FilterPopover({
  title,
  chip,
  isActive,
  onReset,
  children,
}: {
  title: string;
  chip: ReactNode;
  isActive: boolean;
  onReset: () => void;
  children: (close: () => void) => ReactNode;
}) {
  const { t, i18n } = useTranslation('bookings');
  const presentation = useBookingPresentation();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            styles.filterChip,
            'h-11 shrink-0 gap-1.5 rounded-full px-4 text-sm font-medium',
            isActive && 'border-primary/35 bg-primary/5',
          )}
          data-active={isActive}
          data-test="class-filter-chip"
        >
          {chip}
          <ChevronDown className="text-muted-foreground h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        {...presentation}
        align="start"
        dir={i18n.dir()}
        className={cn(
          'w-72 space-y-3 p-4',
          styles.filterPopover,
          presentation.className,
        )}
        data-test="class-filter-popover"
      >
        <p className={cn('text-sm font-semibold', styles.filterPopoverTitle)}>
          {title}
        </p>
        {children(() => setOpen(false))}
        <Button
          type="button"
          variant="link"
          className="h-9 px-0"
          disabled={!isActive}
          onClick={onReset}
          data-test="class-filter-reset"
        >
          {t('classFilters.reset')}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
